import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";

const thisPeriod = () => new Date().toISOString().slice(0, 7);

export default function CapacityPlanner() {
  const [period, setPeriod] = useState(thisPeriod());
  const [plan, setPlan] = useState(null);
  const [hours, setHours] = useState(null);
  const [edits, setEdits] = useState({});
  const [hoursStatus, setHoursStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback((value) => {
    apiFetch(`/capacity/plan?period=${value}`).then(async (r) => {
      const body = await r.json();
      if (!r.ok) {
        setPlan(null);
        setError(typeof body.detail === "string" ? body.detail
                                                 : "Capacity planning is not available to you");
        return;
      }
      setError(null);
      setPlan(body);
    });
    apiFetch("/capacity/hours").then(async (r) => {
      if (r.ok) setHours(await r.json());
    });
  }, []);

  const saveHours = async (row) => {
    const value = Number(edits[row.provider_key]);
    const r = await apiFetch(`/capacity/hours/${row.provider_key}`, {
      method: "PUT",
      body: JSON.stringify({ hours_per_person_per_week: value }),
    });
    const body = await r.json().catch(() => ({}));
    setHoursStatus(r.ok ? body.message
      : (typeof body.detail === "string" ? body.detail : "Could not publish those hours"));
    if (r.ok) {
      setEdits((s) => ({ ...s, [row.provider_key]: "" }));
      load(period);
    }
  };

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load(period);
  }, [navigate, load, period]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="capacity-title">Capacity planner</h1>
          <p className="text-sm text-muted-foreground">
            What each team has committed, against the hours it actually has.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="capacity-queue-link">
            <Link to="/queue">Queue</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="space-y-1 max-w-xs">
          <label htmlFor="capacity-period" className="text-sm font-medium">Month</label>
          <input id="capacity-period" type="month" data-testid="capacity-period"
                 className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                 value={period} onChange={(e) => setPeriod(e.target.value || thisPeriod())} />
        </div>

        {error && <p className="text-sm text-destructive" data-testid="capacity-error">{error}</p>}

        {plan && (
          <>
            <section className="grid gap-3 md:grid-cols-4 text-sm" data-testid="capacity-totals">
              {[["Hours available", plan.totals.available_hours, "available"],
                ["Hours committed", plan.totals.committed_hours, "committed"],
                ["Teams over committed", plan.totals.over_committed_teams, "over"],
                ["Unattributed hours", plan.unattributed_hours, "unattributed"]]
                .map(([label, value, id]) => (
                  <div key={id} className="border rounded-md px-3 py-2"
                       data-testid={`capacity-total-${id}`}>
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
            </section>

            <p className="text-sm text-muted-foreground" data-testid="capacity-method">
              {plan.method}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md" data-testid="capacity-table">
                <caption className="sr-only">Committed work against available hours by team</caption>
                <thead>
                  <tr className="text-left border-b">
                    <th scope="col" className="px-3 py-2">Team</th>
                    <th scope="col" className="px-3 py-2">People</th>
                    <th scope="col" className="px-3 py-2">Hours/person/week</th>
                    <th scope="col" className="px-3 py-2">Available</th>
                    <th scope="col" className="px-3 py-2">Committed</th>
                    <th scope="col" className="px-3 py-2">Logged</th>
                    <th scope="col" className="px-3 py-2">Open requests</th>
                    <th scope="col" className="px-3 py-2">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.rows.map((row) => (
                    <tr key={row.provider_key} className="border-b last:border-0"
                        data-testid={`capacity-row-${row.provider_key}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium">
                        {row.provider_name}
                        <span className="block text-xs text-muted-foreground">
                          {row.hours_source}
                        </span>
                      </th>
                      <td className="px-3 py-2">{row.people}</td>
                      <td className="px-3 py-2">{row.hours_per_person_per_week}</td>
                      <td className="px-3 py-2">{row.available_hours}</td>
                      <td className="px-3 py-2">{row.committed_hours}</td>
                      <td className="px-3 py-2">{row.actual_hours}</td>
                      <td className="px-3 py-2">{row.open_requests}</td>
                      <td className={`px-3 py-2 ${row.over_committed ? "text-red-800 font-medium"
                                                                     : "text-emerald-800"}`}
                          data-testid={`capacity-position-${row.provider_key}`}>
                        {row.no_hours_recorded
                          ? <span className="text-amber-800">no staff recorded</span>
                          : row.over_committed
                            ? `over by ${Math.abs(row.remaining_hours)} h`
                            : `${row.remaining_hours} h left (${row.utilisation_pct}% used)`}
                      </td>
                    </tr>
                  ))}
                  {plan.rows.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-2 text-muted-foreground"
                            data-testid="capacity-empty">
                      No service providers are published yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground" data-testid="capacity-sample">
              Based on {plan.sample_size.demand_entries} demand entr(ies) across{" "}
              {plan.sample_size.providers} team(s) and {plan.sample_size.people} people;{" "}
              {plan.same_linked_entries_excluded} duplicate-linked entr(ies) excluded.
            </p>

            {hours && (
              <section className="space-y-3 border-t pt-6" data-testid="capacity-hours">
                <h2 className="text-base md:text-lg font-medium">Team hours</h2>
                <p className="text-sm text-muted-foreground" data-testid="capacity-hours-method">
                  {hours.method}
                </p>
                {hoursStatus && (
                  <p className="text-sm text-amber-800" data-testid="capacity-hours-status">
                    {hoursStatus}
                  </p>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border rounded-md">
                    <caption className="sr-only">Weekly hours per person by team</caption>
                    <thead>
                      <tr className="text-left border-b">
                        <th scope="col" className="px-3 py-2">Team</th>
                        <th scope="col" className="px-3 py-2">Hours/person/week</th>
                        <th scope="col" className="px-3 py-2">People</th>
                        <th scope="col" className="px-3 py-2">Set a new figure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hours.rows.map((row) => (
                        <tr key={row.provider_key} className="border-b last:border-0"
                            data-testid={`capacity-hours-row-${row.provider_key}`}>
                          <th scope="row" className="px-3 py-2 text-left font-medium">
                            {row.provider_name}
                          </th>
                          <td className="px-3 py-2">
                            {row.hours_per_person_per_week}
                            <span className="block text-xs text-muted-foreground">
                              {row.configured ? "set by this team"
                                              : "platform default until this team sets its own"}
                            </span>
                          </td>
                          <td className="px-3 py-2">{row.people}</td>
                          <td className="px-3 py-2">
                            {row.can_edit ? (
                              <div className="flex items-end gap-2">
                                <div className="space-y-1">
                                  <label htmlFor={`hours-${row.provider_key}`}
                                         className="text-xs block">
                                    Hours for {row.provider_name}
                                  </label>
                                  <input id={`hours-${row.provider_key}`} type="number"
                                         min="1" max="60" step="0.5"
                                         data-testid={`capacity-hours-input-${row.provider_key}`}
                                         className="w-24 border rounded-md px-2 py-1 text-sm bg-background"
                                         value={edits[row.provider_key] ?? ""}
                                         onChange={(e) => setEdits({ ...edits,
                                                                     [row.provider_key]: e.target.value })} />
                                </div>
                                <Button size="sm" variant="outline"
                                        data-testid={`capacity-hours-save-${row.provider_key}`}
                                        disabled={!edits[row.provider_key]}
                                        onClick={() => saveHours(row)}>
                                  Publish
                                </Button>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs"
                                    data-testid={`capacity-hours-readonly-${row.provider_key}`}>
                                outside your administrative scope
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
